import { hashKey } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { ChangeUsersDialog } from '@/features/proactive-agents/ChangeUsersDialog';
import { CollapsibleList } from '@/features/proactive-agents/CollapsibleList';
import { EmailPickerFields } from '@/features/proactive-agents/EmailPickerFields';
import { plural } from '@/features/proactive-agents/plural';
import type {
  UserAction,
  UserAgentChange,
  UserFilter,
  UserPreview,
} from '@/features/proactive-agents/proactive-agents-types';
import { useEmailPicker } from '@/features/proactive-agents/useEmailPicker';
import { useLastUserChange } from '@/features/proactive-agents/useLastUserChange';
import {
  changePhaseOf,
  mayHaveStarted,
  useBuiltinAgents,
  useChangeUsers,
  useUserChangeStatusQuery,
  useUserPreview,
} from '@/features/proactive-agents/useProactiveAgents';
import {
  restartActionLabel,
  USER_ACTIONS,
} from '@/features/proactive-agents/userActions';
import { UserChangeResults } from '@/features/proactive-agents/UserChangeResults';
import { UserPreviewTable } from '@/features/proactive-agents/UserPreviewTable';

// The agent and users a dialog was opened for. A preview that reloads behind the dialog must not change them unseen.
type PendingAction = {
  action: UserAction;
  agent: string;
  filter: UserFilter;
  userCount: number;
  previewKey: string;
};

// The whole preview, so a changed count, default, internal status or chat also counts as a change
const previewKeyOf = (preview: UserPreview | undefined) => hashKey([preview]);

// Setting or clearing the default acts on every matching user, and the other actions on those with a chat of the agent
const userCountOf = (action: UserAction, preview: UserPreview | undefined) =>
  action === 'set-default' || action === 'clear-default'
    ? (preview?.user_count ?? 0)
    : (preview?.users_with_chat_count ?? 0);

const ACTIONS = Object.keys(USER_ACTIONS) as UserAction[];

// Keyed on `adminEmail` by the page, so another admin's last change is read afresh
export const UserAgentsSection = ({ adminEmail }: { adminEmail: string }) => {
  const builtinQuery = useBuiltinAgents();
  const codeDefault = builtinQuery.data?.code_default ?? '';
  const defaultChoices = builtinQuery.data?.default_agent_choices ?? [];
  const [chosenAgent, setChosenAgent] = useState('');
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [lastChange, rememberLastChange] = useLastUserChange(adminEmail);
  const agent = chosenAgent || codeDefault;
  const picker = useEmailPicker(agent);
  const previewQuery = useUserPreview(picker.target);
  const changeUsers = useChangeUsers();
  // Not while the change starts, as its status is not found until the server has saved it
  const statusQuery = useUserChangeStatusQuery(
    changeUsers.isPending || !lastChange
      ? null
      : { id: lastChange.change_id, startedAt: lastChange.started_at }
  );
  const preview = previewQuery.data;
  const stoppedCount = preview?.users_with_stopped_chat_count ?? 0;
  // Whether this admin's last change in this browser may still be changing users, which locks the buttons so that it
  // does not interleave with the next. A change whose status has not loaded, or failed to load, may still be running.
  const lastChangePhase = changePhaseOf(statusQuery.data, statusQuery.error);
  const lastChangeMayBeRunning =
    lastChange !== null &&
    lastChangePhase !== 'not-found' &&
    lastChangePhase !== 'finished';
  // Never change users the admin has not seen for the current input
  const previewIsCurrent =
    picker.inputIsSettled && previewQuery.isSuccess && !previewQuery.isFetching;
  const canChange =
    picker.target !== null &&
    previewIsCurrent &&
    !changeUsers.isPending &&
    !lastChangeMayBeRunning;
  const agentCanBeDefault = defaultChoices.includes(agent);
  // Clearing an override applies the code default, so it is offered while the preview shows the code default's chats
  const canDo = (userAction: UserAction) =>
    canChange &&
    userCountOf(userAction, preview) > 0 &&
    !(userAction === 'clear-default' && agent !== codeDefault) &&
    !(userAction === 'set-default' && !agentCanBeDefault);
  const lastChangeMatchesTarget =
    lastChange !== null &&
    hashKey([lastChange.target]) === hashKey([picker.target]);
  // A running change stays in view, as it holds the buttons. Otherwise its results show while the agent and users
  // picked are the ones it was made for.
  const showsLastChange = lastChangeMayBeRunning || lastChangeMatchesTarget;

  const handleConfirm = (change: UserAgentChange) => {
    setPending(null);
    // Remembered before the request, so the page follows the change also when the request breaks off or the page is
    // left meanwhile
    const changeId = crypto.randomUUID();
    rememberLastChange({
      change_id: changeId,
      name: USER_ACTIONS[change.action].name(change.agent),
      target: picker.target,
      started_at: Date.now(),
    });
    changeUsers.mutate(
      { changeId, change },
      {
        // A refused request started nothing, so there is no change to follow
        onError: (error) => {
          if (!mayHaveStarted(error)) rememberLastChange(null);
        },
      }
    );
  };

  return (
    <Card role="region" aria-labelledby="user_agents_title">
      <CardHeader>
        <CardTitle id="user_agents_title">Change users&apos; agents</CardTitle>
        <CardDescription>
          Pick an agent and users, check the preview, then set or clear their
          default agent, or pause, resume or delete their chat of the agent.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6">
        {builtinQuery.error ? (
          <p role="alert" className="text-sm text-destructive">
            {builtinQuery.error.message}
          </p>
        ) : null}
        <div className="grid max-w-3xl gap-6">
          <div className="grid gap-2 text-sm">
            <label htmlFor="user_agent">Agent</label>
            <Select
              id="user_agent"
              value={agent}
              onChange={(event) => setChosenAgent(event.target.value)}
              disabled={!builtinQuery.data}
            >
              {builtinQuery.data?.agents.map((builtinAgent) => (
                <option key={builtinAgent} value={builtinAgent}>
                  {builtinAgent}
                  {builtinAgent === codeDefault ? ' (code default)' : ''}
                </option>
              ))}
            </Select>
            {builtinQuery.isLoading ? (
              <p className="text-muted-foreground">Loading...</p>
            ) : null}
          </div>

          <EmailPickerFields label="Users" picker={picker} />
        </div>

        {picker.target && preview ? (
          <div className="grid gap-3 text-sm">
            <p>
              {plural(preview.user_count, 'user')},{' '}
              {preview.users_with_chat_count.toLocaleString()} with a chat of{' '}
              {agent}
              {stoppedCount
                ? `, ${stoppedCount.toLocaleString()} of them stopped`
                : ''}
              .
            </p>
            <p className="text-muted-foreground">
              Pause, Resume and Delete chat act only on the users with a chat of{' '}
              {agent}.
            </p>
            {stoppedCount ? (
              <>
                {agentCanBeDefault ? (
                  <p className="text-muted-foreground">
                    A stopped chat blocks starting {agent} again. To start it
                    for these users, pick only them under Specific emails,
                    delete their chat, then use{' '}
                    {restartActionLabel(agent, codeDefault)} with apply now,
                    which also sets their default. An internal user can instead
                    press Run now for it in their settings after the deletion,
                    which keeps their default.
                  </p>
                ) : (
                  <p className="text-muted-foreground">
                    A stopped chat blocks starting {agent} again. Once the chat
                    is deleted, an internal user can press Run now for it in
                    their settings.
                  </p>
                )}
                <CollapsibleList
                  title={`Stopped chat of ${agent}`}
                  count={stoppedCount}
                  items={preview.stopped_chat_emails.map((email) => ({
                    key: email,
                    text: email,
                  }))}
                  open
                />
                {stoppedCount > preview.stopped_chat_emails.length ? (
                  <p className="text-muted-foreground">
                    The list shows the first{' '}
                    {preview.stopped_chat_emails.length.toLocaleString()} by
                    email.
                  </p>
                ) : null}
              </>
            ) : null}
            {preview.users.length ? (
              <UserPreviewTable
                agent={agent}
                users={preview.users}
                userCount={preview.user_count}
              />
            ) : null}
            <CollapsibleList
              title="No active user has these emails"
              items={preview.unmatched_emails.map((email) => ({
                key: email,
                text: email,
              }))}
              open
            />
          </div>
        ) : null}
        {previewQuery.error ? (
          <p role="alert" className="text-sm text-destructive">
            {previewQuery.error.message}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {ACTIONS.map((buttonAction) => (
            <Button
              key={buttonAction}
              type="button"
              variant={buttonAction === 'delete' ? 'destructive' : 'outline'}
              disabled={!canDo(buttonAction)}
              onClick={() =>
                picker.target &&
                setPending({
                  action: buttonAction,
                  agent,
                  filter: {
                    emails: picker.target.emails,
                    email_contains: picker.target.email_contains,
                  },
                  userCount: userCountOf(buttonAction, preview),
                  previewKey: previewKeyOf(preview),
                })
              }
            >
              {USER_ACTIONS[buttonAction].label}
            </Button>
          ))}
        </div>
        {agent !== codeDefault ? (
          <p className="text-sm text-muted-foreground">
            Clear override removes the picked users&apos; overrides, so their
            default agent becomes the code default, {codeDefault}. It is offered
            only with {codeDefault} picked as the agent.
          </p>
        ) : null}
        {builtinQuery.data && !agentCanBeDefault ? (
          <p className="text-sm text-muted-foreground">
            {agent} cannot be chosen as a default, so Set as default is not
            offered.
          </p>
        ) : null}
        {changeUsers.isPending ? (
          <p className="text-sm text-muted-foreground">
            Starting the change...
          </p>
        ) : null}
        {/* Shown until the next change starts, or the change is dismissed */}
        {changeUsers.error ? (
          <div role="alert" className="grid gap-1 text-sm text-destructive">
            <p>{changeUsers.error.message}</p>
            {mayHaveStarted(changeUsers.error) ? (
              <p>
                The change may have started anyway. If it did, its progress
                shows below.
              </p>
            ) : null}
          </div>
        ) : null}
        {lastChange && showsLastChange && !changeUsers.isPending ? (
          <UserChangeResults
            name={lastChange.name}
            phase={lastChangePhase}
            status={statusQuery.data}
            error={statusQuery.error}
            onDismiss={() => {
              rememberLastChange(null);
              changeUsers.reset();
            }}
          />
        ) : null}
      </CardContent>

      {pending ? (
        <ChangeUsersDialog
          action={pending.action}
          agent={pending.agent}
          codeDefault={codeDefault}
          agentCanBeDefault={defaultChoices.includes(pending.agent)}
          filter={pending.filter}
          userCount={pending.userCount}
          previewChanged={
            pending.agent !== agent ||
            pending.previewKey !== previewKeyOf(preview)
          }
          canConfirm={canDo(pending.action)}
          onConfirm={handleConfirm}
          onCancel={() => setPending(null)}
        />
      ) : null}
    </Card>
  );
};
